import React, { useState, useContext, useEffect } from 'react';

import {
  Paper,
  Button,
  TextField,
  Typography,
  Box,
  Container,
  Stack,
  Stepper,
  Step,
  StepButton,
  Slide,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  convertNewlinesToBreaks,
  calculateRowHeight,
  formatLetterDate,
} from '../../utils/utils';
import { AuthContext } from '../../context/authcontext';
import { FileCopy, BookmarkAddOutlined, BookmarkAdded } from '@mui/icons-material';
import DrawIcon from '@mui/icons-material/Draw';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import SaveLetterModal from '../../components/SaveLetterModal/SaveLetterModal';

export default function ExistingLetter({
  refinedLetter,
  setPrevLetter,
  setJobInfo,
  setCompanyInfo,
  companyInfo,
  jobInfo,
  setOpenAuthenticationModal,
  setOpenRefineLetterModal,
  handleSubmit,
  handleSaveLetter,
}) {
  const { currentUser, prevLetter } = useContext(AuthContext);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveSnackOpen, setSaveSnackOpen] = useState(false);
  const [saveError, setSaveError] = useState(null);
  useEffect(() => {
    setSaved(false);
  }, [refinedLetter]);

  const suggestedName = `Cover letter · ${formatLetterDate(
    new Date().toISOString(),
  )}`;

  const onSaveLetter = async (name) => {
    setSaving(true);
    setSaveError(null);
    const res = await handleSaveLetter(name);
    setSaving(false);
    if (res && res.letter) {
      setSaved(true);
      setSaveModalOpen(false);
      setSaveSnackOpen(true);
    } else {
      setSaveError((res && res.error) || 'Could not save. Please try again.');
    }
  };
  const steps = [
    'Paste Existing Cover Letter',
    'Add Company Description',
    'Add Job Description',
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);

  // useEffect(() => {
  //   // define the function that will be called on window resize
  //   function handleResize() {
  //     setNumRows(calculateRowHeight());
  //   }

  //   // set up the event listener for window resize
  //   window.addEventListener('resize', handleResize);

  //   // specify how to clean up after this effect
  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //   };
  // }, []); // Empty array means that effect runs only on mount and unmount

  const handleNext = () => {
    const newActiveStep = activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(refinedLetter || placeholder)
      .then(() => {
        setOpen(true);
      })
      .catch((err) => {
        // Couldn't copy text
        console.log('Failed to copy text: ', err);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const textFieldStyle = {
    width: '100%',
    '& .MuiFormHelperText-root': {
      marginBottom: '.5rem',
      textAlign: 'center',
      margin: '.5rem 0',
      fontSize: { xs: '0.55rem', sm: '.75rem', md: '1rem' },
      width: '100%',
    },
  };

  const placeholder =
    "Dear Hiring Manager, \n\nAs someone with a deep passion for the intersection of technology and sustainable solutions, I was thrilled to come across the opening for [position] at [company]. I am eager to bring my unique blend of entrepreneurial grit, technical expertise, and hands-on experience to the role and contribute to [company's] continued growth and success.\n\nAt the core of my journey into the tech sector lies a commitment to using innovation to propel meaningful change. As the solo founder of a travel tech company, I developed an application from scratch with zero prior experience. While the venture didn't ultimately succeed, it ignited my passion for technology and taught me invaluable lessons in resilience, adaptability, and the importance of user-centered design.\n\nFollowing this initial foray, I took on a position at [Company] with only 4 months of coding experience. Here, I rapidly grew from a novice to a significant contributor within the software development team, taking on mid-level responsibilities such as leading refactors, aiding management in architectural decisions, and mentoring junior developers. This experience not only honed my technical expertise but also fortified my ability to effectively collaborate and drive progress within a team.\n\nIn my current pursuit of a role in a company committed to clean energy, I am thrilled by the opportunity to bring my skillset to the mission of converting the US power grid to be 100% fossil-free. As someone with a deep passion for renewable solutions, I am excited by the prospect of working on the forefront of innovation at [company]. I am energized by the chance to lead new product work, engage directly with users, and collaborate on scoping new features and engineering plans. \n\nAs a driven and adaptable individual with a proven history of success in high-growth tech companies, I believe I would be a valuable asset to the [company] team. I am committed to utilizing my unique blend of skills to drive progress towards a more sustainable future. Thank you for considering my application and I look forward to discussing my qualifications further.\n\nSincerely, \n[Your name]";

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Header />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Stack
          sx={{ margin: '2rem' }}
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
        >
          <Slide direction="right" in={true} timeout={1000} unmountOnExit>
            <Paper
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: 2,
                elevation: 3,
                p: { xs: 2, sm: 4 },
                m: { xs: '0', md: '0 2rem' },
              }}
            >
              <Box sx={{ width: '100%', marginBottom: 4 }}>
                <Stepper nonLinear activeStep={activeStep}>
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepButton
                        sx={{
                          '& span': {
                            fontSize: {
                              xs: '.55rem',
                              sm: '.75rem',
                              md: '1rem',
                            },
                          },
                        }}
                        color="inherit"
                        onClick={handleStep(index)}
                      >
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TextField
                  label="Paste existing cover letter"
                  defaultValue={prevLetter}
                  helperText={`${
                    prevLetter?.length === 4000
                      ? '4000/4000 Char. You have reached the maximum amount of characters for your cover letter'
                      : 'Please enter your existing cover letter, which will be modified for your new application'
                  }`}
                  multiline
                  rows={calculateRowHeight()}
                  variant="outlined"
                  sx={textFieldStyle}
                  onChange={(e) => setPrevLetter(e.target.value)}
                  style={{ display: activeStep === 0 ? 'flex' : 'none' }}
                  inputProps={{
                    maxLength: 4000,
                  }}
                />
                <TextField
                  label="Share Company Description"
                  helperText={`${
                    companyInfo?.length === 5000
                      ? '5000/5000 Char. You have reached the maximum amount of characters for your description'
                      : 'Provide details of the company you are applying to, such as accomplishments and merits'
                  }`}
                  multiline
                  rows={calculateRowHeight()}
                  variant="outlined"
                  sx={textFieldStyle}
                  onChange={(e) => setCompanyInfo(e.target.value)}
                  style={{ display: activeStep === 1 ? 'flex' : 'none' }}
                  inputProps={{
                    maxLength: 5000,
                  }}
                />
                <TextField
                  label="Share Job Description"
                  helperText={`${
                    jobInfo?.length === 5000
                      ? '5000/5000 Char. You have reached the maximum amount of characters for your description'
                      : 'Provide job description for which you are applying, such as personality traits and skills'
                  }`}
                  multiline
                  rows={calculateRowHeight()}
                  variant="outlined"
                  sx={textFieldStyle}
                  onChange={(e) => setJobInfo(e.target.value)}
                  style={{ display: activeStep === 2 ? 'flex' : 'none' }}
                  inputProps={{
                    maxLength: 5000,
                  }}
                />
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmit}
                    sx={{
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    Generate Cover Letter
                  </Button>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      pt: 2,
                    }}
                  >
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Button onClick={handleNext} sx={{ mr: 1 }}>
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Slide>

          <Slide direction="left" in={true} timeout={1000} unmountOnExit>
            <Container
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: 'white',
                justifyContent: 'flex-start',
                p: 4,
                paddingBottom: 6,
                boxShadow: '10px 10px 20px rgba(0, 0, 0, 0.3)',
                width: { xs: '100%', lg: '42vw' },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ color: 'black', fontWeight: 'bold' }}
                >
                  {refinedLetter
                    ? 'Your AI Cover Letter'
                    : 'Example Cover Letter'}
                </Typography>
                <Tooltip title="Copy Cover Letter">
                  <IconButton onClick={handleCopy}>
                    <FileCopy />
                  </IconButton>
                </Tooltip>
                {refinedLetter && currentUser && (
                  <Tooltip title="Refine Cover Letter">
                    <IconButton onClick={() => setOpenRefineLetterModal(true)}>
                      <DrawIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {refinedLetter && currentUser && (
                  <Tooltip
                    title={saved ? 'Saved to My Letters' : 'Save to My Letters'}
                  >
                    <IconButton
                      onClick={() => !saved && setSaveModalOpen(true)}
                      color={saved ? 'success' : 'default'}
                    >
                      {saved ? <BookmarkAdded /> : <BookmarkAddOutlined />}
                    </IconButton>
                  </Tooltip>
                )}
                <Snackbar
                  open={open}
                  autoHideDuration={4000}
                  onClose={handleClose}
                >
                  <Alert
                    onClose={handleClose}
                    severity="success"
                    sx={{ width: '100%', maxWidth: 400 }}
                  >
                    Text copied! Always make sure you proofread your cover
                    letter as AI could 'hallucinate' and make mistakes.
                  </Alert>
                </Snackbar>
              </Stack>
              <Box
                component="div"
                sx={{
                  color: '#333',
                  p: 2,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="subtitle1"
                  align="justify"
                  component="p"
                  sx={{ maxHeight: '65vh' }}
                >
                  {convertNewlinesToBreaks(refinedLetter || placeholder)}
                </Typography>
              </Box>
              {refinedLetter && !currentUser && (
                <Button
                  onClick={() => setOpenAuthenticationModal(true)}
                  variant="outlined"
                  color="primary"
                  sx={{ m: 1 }}
                >
                  Sign in for free to see more...
                </Button>
              )}
            </Container>
          </Slide>
        </Stack>
      </Box>

      <SaveLetterModal
        open={saveModalOpen}
        handleClose={() => !saving && setSaveModalOpen(false)}
        onSave={onSaveLetter}
        defaultName={suggestedName}
        saving={saving}
      />

      <Snackbar
        open={saveSnackOpen}
        autoHideDuration={5000}
        onClose={() => setSaveSnackOpen(false)}
      >
        <Alert
          onClose={() => setSaveSnackOpen(false)}
          severity="success"
          sx={{ width: '100%', maxWidth: 420 }}
          action={
            <Button
              component={Link}
              to="/letters"
              color="inherit"
              size="small"
            >
              View
            </Button>
          }
        >
          Saved to My Letters.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveError}
        autoHideDuration={5000}
        onClose={() => setSaveError(null)}
      >
        <Alert
          onClose={() => setSaveError(null)}
          severity="error"
          sx={{ width: '100%', maxWidth: 420 }}
        >
          {saveError}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
}
